import * as React from 'react'
import { cn } from '../utils'

export type LayoutType = 'classic' | 'sidebar-left' | 'sidebar-right' | 'two-column'

export interface LayoutOption {
  id: LayoutType
  name: string
  description: string
  preview: React.ReactNode
}

const LAYOUTS: LayoutOption[] = [
  {
    id: 'classic',
    name: 'Classic',
    description: 'Full-width sections',
    preview: (
      <div className="w-full h-full flex flex-col gap-1 p-1">
        <div className="h-3 bg-gray-400 rounded-sm" />
        <div className="h-2 bg-gray-300 rounded-sm" />
        <div className="flex-1 bg-gray-200 rounded-sm" />
        <div className="flex-1 bg-gray-200 rounded-sm" />
      </div>
    ),
  },
  {
    id: 'sidebar-left',
    name: 'Sidebar Left',
    description: 'Photo & skills on left',
    preview: (
      <div className="w-full h-full flex gap-1 p-1">
        <div className="w-1/3 flex flex-col gap-1">
          <div className="h-4 bg-gray-400 rounded-sm" />
          <div className="flex-1 bg-gray-300 rounded-sm" />
        </div>
        <div className="flex-1 flex flex-col gap-1">
          <div className="h-2 bg-gray-300 rounded-sm" />
          <div className="flex-1 bg-gray-200 rounded-sm" />
          <div className="flex-1 bg-gray-200 rounded-sm" />
        </div>
      </div>
    ),
  },
  {
    id: 'sidebar-right',
    name: 'Sidebar Right',
    description: 'Photo & skills on right',
    preview: (
      <div className="w-full h-full flex gap-1 p-1">
        <div className="flex-1 flex flex-col gap-1">
          <div className="h-2 bg-gray-300 rounded-sm" />
          <div className="flex-1 bg-gray-200 rounded-sm" />
          <div className="flex-1 bg-gray-200 rounded-sm" />
        </div>
        <div className="w-1/3 flex flex-col gap-1">
          <div className="h-4 bg-gray-400 rounded-sm" />
          <div className="flex-1 bg-gray-300 rounded-sm" />
        </div>
      </div>
    ),
  },
  {
    id: 'two-column',
    name: 'Two Column',
    description: 'Split content layout',
    preview: (
      <div className="w-full h-full flex flex-col gap-1 p-1">
        <div className="h-3 bg-gray-400 rounded-sm" />
        <div className="flex-1 flex gap-1">
          <div className="flex-1 flex flex-col gap-1">
            <div className="flex-1 bg-gray-200 rounded-sm" />
            <div className="flex-1 bg-gray-200 rounded-sm" />
          </div>
          <div className="flex-1 flex flex-col gap-1">
            <div className="flex-1 bg-gray-300 rounded-sm" />
            <div className="flex-1 bg-gray-300 rounded-sm" />
          </div>
        </div>
      </div>
    ),
  },
]

export interface LayoutSelectorProps {
  value: LayoutType
  onChange: (layout: LayoutType) => void
  className?: string
}

export function LayoutSelector({ value, onChange, className }: LayoutSelectorProps) {
  return (
    <div className={cn('space-y-3', className)} role="radiogroup" aria-label="CV Layout">
      <label className="text-sm font-medium text-gray-700">
        Layout Template
      </label>
      <div className="grid grid-cols-2 gap-3">
        {LAYOUTS.map((layout) => (
          <button
            key={layout.id}
            type="button"
            role="radio"
            aria-checked={value === layout.id}
            onClick={() => onChange(layout.id)}
            className={cn(
              'cursor-pointer rounded-lg border-2 p-2',
              'hover:border-gray-300',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
              'transition-all duration-150 ease-out',
              value === layout.id
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200'
            )}
          >
            <div className="aspect-[3/4] w-full bg-white border border-gray-200 rounded mb-2 overflow-hidden">
              {layout.preview}
            </div>
            <div className="text-center">
              <div className={cn(
                'text-sm font-medium',
                value === layout.id ? 'text-primary-700' : 'text-gray-900'
              )}>
                {layout.name}
              </div>
              <div className="text-xs text-gray-500">{layout.description}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
